import { useMemo } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { useAssets, type AssetRow } from '../data/fleet.ts'
import { useAreas, type AreaRow } from '../data/geo.ts'
import { paramSummary, settingLines, usePlan, type PlanJob, type SettingLine } from '../data/plan.ts'
import { SHEET_LINES_PER_PAGE, areasSummary, longDay, sheetPageBreaks, timeShort, useNames } from '../data/planUi.ts'
import { localDate } from '../lib/time.ts'
import { BackLink } from '../ui/components.tsx'
import { LookStrip } from '../ui/looks/LookStrip.tsx'
import { frameOfAreas } from './plan/JobSettings.tsx'
import { groupName, initials } from './plan/shared.ts'

const ISO = /^\d{4}-\d{2}-\d{2}$/

interface Line { job: PlanJob; group: number; time: string; zones: string; machines: string; crew: string; settings: string; settingLines: SettingLine[]; frame: ReturnType<typeof frameOfAreas> }

/** The daily sheet to print and pin up in the shed or hand to a crew with no phone: one line per job (M23 item 13: the old sheet listed every area of
 *  every job and ran to six A4 pages): series · job · zones summarised · machine tag · crew initials · settings, a box to tick. The table from `sm:`
 *  and on paper; on a phone the same lines as cards. Built from the device's own copy, so it prints with no network.
 *  On paper (A4, 8-mm margins, `index.css`): a 10-pt line per job at line-height 1.2 with 1 px of padding, its settings on a 9-pt line under it, so that
 *  `SHEET_LINES_PER_PAGE` jobs fit one page with room for wrapped cells (the arithmetic is on the constant, `data/planUi.ts`); a series that would straddle
 *  a page starts the next one (`sheetPageBreaks`), a job and its settings never split (one `<tbody>` each, `break-inside-avoid`). */
export function PlanPrintPage() {
  const { t, i18n } = useTranslation()
  const { org, site } = useClub()
  const lang = i18n.language
  const search = useSearch({ strict: false }) as { d?: string }
  const date = search.d && ISO.test(search.d) ? search.d : localDate(new Date(), site.timezone)
  const { ready, plan, taskTypes } = usePlan(lang)
  const names = useNames(); const areas = useAreas(); const assets = useAssets()
  const areaById = useMemo(() => new Map((areas ?? []).map((a) => [a.id, a as AreaRow])), [areas])
  const assetById = useMemo(() => new Map((assets ?? []).map((a) => [a.id, a as AssetRow])), [assets])
  if (!ready || !names || !areas || !assets) return <p>{t('common.loading')}</p>
  const typeByCode = new Map(taskTypes.map((x) => [x.code, x]))
  const lines: Line[] = plan.day(date).map((j) => ({
    job: j, group: j.sequence_group, time: timeShort(j.scheduled_start_local),
    zones: areasSummary(j.areaIds, areaById, lang, t),
    machines: [...new Set(j.crew.flatMap((a) => (a.asset_id ? [assetById.get(a.asset_id)?.readable_tag ?? '?'] : [])))].join(', '),
    crew: j.crew.map((a) => initials(names.get(a.person_id) ?? '?')).join(', '),
    settings: paramSummary(j.params, typeByCode.get(j.task_type_code), lang),
    settingLines: settingLines(j.params, typeByCode.get(j.task_type_code), lang), frame: frameOfAreas(j.areaIds, areaById),
  }))
  const breaks = sheetPageBreaks(lines.map((l) => l.group), SHEET_LINES_PER_PAGE)
  const firstOfGroup = (i: number) => i === 0 || lines[i - 1]!.group !== lines[i]!.group
  return (
    <section className="plan-sheet flex flex-col gap-4 print:gap-2 print:text-[10pt] print:leading-[1.2]" data-testid="plan-sheet" data-lines={lines.length}>
      {/* its print page (A4, 8-mm margins, black on white) is in index.css: the CSP refuses a <style> element */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <BackLink to="/plan" search={{ d: date }} testId="sheet-back" className="sm:w-auto">← {t('plan.back')}</BackLink>
        <button className="btn btn-primary" onClick={() => window.print()} data-testid="sheet-print">{t('plan.printNow')}</button>
      </div>
      <header className="flex flex-col gap-0.5 border-b-2 border-slate-900 pb-2 dark:border-slate-100 print:border-black print:pb-1">
        <p className="text-sm print:text-[10pt]">{org.org_name} · {site.name}</p>
        <h1 className="text-2xl font-bold first-letter:uppercase print:text-[14pt]">{t('plan.sheetTitle')} — {longDay(date, lang)}</h1>
      </header>
      {lines.length === 0 && <p data-testid="sheet-empty">{t('plan.empty')}</p>}
      {lines.length > 0 && (
        <ul className="flex flex-col gap-2 sm:hidden print:hidden" data-testid="sheet-cards">
          {lines.map((l, i) => (
            <li key={l.job.id} className="card flex flex-col gap-1" data-testid="sheet-card" data-group={l.group}>
              {firstOfGroup(i) && <p className="text-sm font-bold uppercase tracking-wide">{groupName(t, l.group)}</p>}
              <p className="font-semibold">{l.job.title_text}{l.time && <span className="font-normal"> · {l.time}</span>}</p>
              {l.job.notes && <p className="meta italic">{l.job.notes}</p>}
              <p>{l.zones}</p>
              <p className="meta">{[l.machines, l.crew || t('plan.nobody'), l.settings].filter(Boolean).join(' · ')}</p>
            </li>
          ))}
        </ul>
      )}
      {lines.length > 0 && (
        // the line-height on paper is set here too: `text-sm` carries its own (1.43), which would stay on paper and never let the section's 1.2 through
        <table className="hidden w-full border-collapse text-left text-sm sm:table print:table print:text-[10pt] print:leading-[1.2]" data-testid="sheet-table">
          <thead>
            <tr className="border-b-2 border-slate-900 dark:border-slate-100 print:border-black">
              <th className="w-10 p-1 text-center print:py-0.5">{t('plan.group')}</th>
              <th className="w-[34%] p-1 print:py-0.5">{t('plan.sheetJob')}</th>
              <th className="w-[30%] p-1 print:py-0.5">{t('plan.areas')}</th>
              <th className="w-[14%] p-1 print:py-0.5">{t('plan.sheetMachine')}</th>
              <th className="w-[12%] p-1 print:py-0.5">{t('plan.crew')}</th>
              <th className="w-6 p-1 text-center print:py-0.5">✓</th>
            </tr>
          </thead>
          {/* one body per job keeps its two rows together on paper: the line, then the settings (a mowing job's four) on one full-width 10-pt line under it
              (review of item 13: as a column, the settings wrapped over ten lines at 113 px and a 17-job day printed four pages) */}
          {lines.map((l, i) => {
            const first = firstOfGroup(i) && i > 0
            const border = first ? 'border-t-2 border-slate-900 dark:border-slate-100 print:border-black' : 'border-t border-slate-400'
            return (
              <tbody key={l.job.id} className={`break-inside-avoid ${breaks.has(i) ? 'break-before-page' : ''}`} data-page-break={breaks.has(i) || undefined}>
                <tr className={`align-top ${border}`} data-testid="sheet-job" data-group={l.group}>
                  <td className="p-1 text-center font-semibold print:py-px">{l.group}</td>
                  <td className="p-1 print:py-px"><strong>{l.job.title_text}</strong>{l.time && ` · ${l.time}`}{l.job.notes && <span className="italic"> — {l.job.notes}</span>}</td>
                  <td className="p-1 print:py-px">{l.zones}</td>
                  <td className="p-1 print:py-px">{l.machines}</td>
                  <td className="p-1 print:py-px">{l.crew || '—'}</td>
                  <td className="p-1 text-center print:py-px"><span className="inline-block size-3.5 border border-current align-middle" /></td>
                </tr>
                {l.settings && (
                  <tr data-testid="sheet-settings">
                    <td />
                    <td colSpan={5} className="px-1 pb-1 text-slate-700 dark:text-slate-300 print:pb-px print:text-[9pt] print:text-black">{t('plan.settings')} : <LookStrip lines={l.settingLines} frame={l.frame} mode="values" testId="sheet-settings-values" /></td>
                  </tr>
                )}
              </tbody>
            )
          })}
        </table>
      )}
    </section>
  )
}
